/**
 * The plan and line details a breakdown states (breakdown accuracy, 2026-10).
 *
 * One definition of each field's label and choices, for the policy panel, the
 * procedure table and the exports. Mirrors the backend's
 * InsurancePolicy.PLAN_DETAIL_FIELDS and PatientProcedureCoverage.LINE_DETAIL_FIELDS.
 *
 * Choice values: "" = never recorded; "unknown" = asked, and the carrier
 * couldn't say. Both are shown, differently: a blank invites the question, an
 * "Unknown" records that it was asked.
 */

const ANSWER = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unknown", label: "Unknown" },
];

export const PLAN_DETAIL_GROUPS = [
  {
    title: "Coverage",
    rows: [
      {
        field: "coverage_scope", label: "Single or family", type: "choice",
        options: [{ value: "single", label: "Single" }, { value: "family", label: "Family" },
          { value: "unknown", label: "Unknown" }],
      },
      {
        field: "benefit_period_type", label: "Benefit year type", type: "choice",
        options: [{ value: "calendar_year", label: "Calendar year" },
          { value: "policy_year", label: "Policy year" }, { value: "unknown", label: "Unknown" }],
      },
      {
        field: "max_scope", label: "Maximums apply", type: "choice",
        options: [{ value: "per_person", label: "Per person" },
          { value: "per_family", label: "Per family" }, { value: "unknown", label: "Unknown" }],
      },
      { field: "perio_endo_in_basic", label: "Perio & endo in Basic", type: "choice", options: ANSWER },
      {
        field: "specialist_fee_scope", label: "Specialist fees covered for", type: "text",
        placeholder: "e.g. hygienist and denturist only",
      },
      { field: "assignment_of_benefits", label: "Pays the office directly", type: "choice", options: ANSWER },
      { field: "predetermination_threshold", label: "Predetermination above ($)", type: "decimal" },
      { field: "coverage_end_date", label: "Coverage ends", type: "date" },
      {
        field: "waiting_period_note", label: "Waiting periods", type: "text",
        placeholder: "As the carrier says it",
      },
    ],
  },
  {
    title: "Ages",
    rows: [
      { field: "age_limit", label: "Member covered to age", type: "integer" },
      { field: "dependent_age_limit", label: "Children covered to age", type: "integer" },
      { field: "student_age_limit", label: "Students covered to age", type: "integer" },
    ],
  },
  {
    title: "Orthodontics",
    rows: [
      { field: "ortho_age_min", label: "Ortho from age", type: "integer" },
      { field: "ortho_age_max", label: "Ortho to age", type: "integer" },
      {
        field: "ortho_payment", label: "Ortho payment", type: "choice",
        options: [{ value: "lump_sum", label: "Lump sum allowed" },
          { value: "instalments", label: "Instalments only" }, { value: "unknown", label: "Unknown" }],
      },
      { field: "ortho_initial_payment_pct", label: "Paid on initial fee (%)", type: "decimal" },
    ],
  },
  {
    title: "Clauses",
    rows: [
      { field: "missing_tooth_clause", label: "Missing tooth clause", type: "choice", options: ANSWER },
      { field: "alternate_benefit", label: "Alternate benefit", type: "choice", options: ANSWER },
      {
        field: "alternate_benefit_note", label: "Alternate benefit applies to", type: "text",
        placeholder: "e.g. white fillings on molars paid at the silver rate",
      },
      {
        field: "implants_coverage", label: "Implants", type: "choice",
        options: [{ value: "covered", label: "Covered" }, { value: "not_covered", label: "Not covered" },
          { value: "estimate_required", label: "Estimate required" }, { value: "unknown", label: "Unknown" }],
      },
      { field: "implants_percentage", label: "Implants (%)", type: "decimal" },
    ],
  },
];

export const PLAN_DETAIL_ROWS = PLAN_DETAIL_GROUPS.reduce(function (all, group) {
  return all.concat(group.rows);
}, []);

export const LINE_CHOICES = {
  coverage_status: [
    { value: "covered", label: "Covered" },
    { value: "not_covered", label: "Not covered" },
    { value: "estimate_required", label: "Estimate required" },
    { value: "predetermination_required", label: "Predetermination required" },
    { value: "conditional", label: "Conditional" },
    { value: "unknown", label: "Unknown" },
  ],
  frequency_basis: [
    { value: "rolling", label: "Rolling" },
    { value: "calendar_year", label: "Calendar year" },
    { value: "benefit_year", label: "Benefit year" },
    { value: "lifetime", label: "Lifetime" },
    { value: "unknown", label: "Unknown" },
  ],
  limit_scope: [
    { value: "per_person", label: "Per person" },
    { value: "per_tooth", label: "Per tooth" },
    { value: "per_surface", label: "Per surface" },
  ],
  limit_unit: [
    { value: "occurrences", label: "Occurrences" },
    { value: "time_units", label: "Time units" },
  ],
  // THIS procedure is paid as a cheaper alternative ("white fillings on molars
  // paid at the silver rate"). Not the plan-wide "least expensive alternate
  // procedure" clause, which Sun Life prints on every line of every plan.
  alternate_benefit: [
    { value: "yes", label: "Paid as an alternate" },
    { value: "no", label: "No alternate benefit" },
    { value: "unknown", label: "Alternate benefit unknown" },
  ],
};

/**
 * The plan's own year, as words: "calendar year", "benefit year", or "" when
 * the plan's year isn't known. A limit "per year" with no qualifier resets
 * with the PLAN's year — the calendar only on a calendar-year plan (Cleer's
 * Jun–May Sun Life plans say "per benefit year"). Reads the server's current
 * `benefit_period` first, then the stored type.
 */
export function planYearWord(policy) {
  const p = policy || {};
  const type = (p.benefit_period && p.benefit_period.type) || p.benefit_period_type;
  return type === "calendar_year" ? "calendar year" : type === "policy_year" ? "benefit year" : "";
}

/** A line's alternate benefit as the table and the exports say it ("" when never recorded). */
export function alternateBenefitWords(value) {
  return choiceLabel(LINE_CHOICES.alternate_benefit, value);
}

/**
 * "01204, 01205" (or spaces, semicolons) as a list of codes; `bad` keeps what
 * isn't a 4–6 digit procedure code so the form can say which.
 */
export function parseCodeList(text) {
  const parts = String(text == null ? "" : text).split(/[\s,;/]+/).filter(Boolean);
  const codes = [];
  const bad = [];
  parts.forEach(function (part) {
    if (!/^\d{4,6}$/.test(part)) bad.push(part);
    else if (codes.indexOf(part) === -1) codes.push(part);
  });
  codes.sort();
  return { codes: codes, bad: bad };
}

/**
 * A limit several codes share, in words: "shared with 01204, 01205 · 2 every
 * 12 months combined" — or "" when the line shares nothing.
 */
export function describeSharedLimit(record) {
  const r = record || {};
  const codes = Array.isArray(r.shared_limit_codes) ? r.shared_limit_codes.filter(Boolean) : [];
  const count = r.shared_limit_count;
  const months = r.shared_limit_months;
  let limit = "";
  if (count != null && count !== "") {
    limit = count + (months != null && months !== "" ? " every " + months + " months" : "") + " combined";
  } else if (months != null && months !== "") {
    limit = "every " + months + " months combined";
  }
  const shared = codes.length ? "shared with " + codes.join(", ") : "";
  return [shared, limit].filter(Boolean).join(" · ");
}

export function choiceLabel(options, value) {
  if (value == null || value === "") return "";
  const match = (options || []).find(function (o) { return o.value === value; });
  return match ? match.label : String(value);
}

/** A plan detail as display text; "" when never recorded. */
export function planDetailText(row, value) {
  if (value == null || value === "") return "";
  if (row.type === "choice") return choiceLabel(row.options, value);
  if (row.type === "decimal" && /%/.test(row.label)) return String(Number(value)) + "%";
  if (row.type === "decimal" && /\$/.test(row.label)) return "$" + Number(value).toLocaleString();
  return String(value);
}

function ageRange(tier) {
  const min = tier.min_age, max = tier.max_age;
  if (min == null && max == null) return "";
  if (min == null) return "to age " + max;
  if (max == null) return "age " + min + "+";
  return "ages " + min + "–" + max;
}

/**
 * Age tiers as one line: "to age 16: 1 every 12 months · age 17+: 4 every 12 months",
 * or "to age 18" for a line covered only within an age range.
 */
export function describeAgeTiers(tiers) {
  if (!Array.isArray(tiers) || !tiers.length) return "";
  return tiers.map(function (tier) {
    const range = ageRange(tier);
    let limit = "";
    if (tier.count != null) limit = tier.count + (tier.months != null ? " every " + tier.months + " months" : "");
    else if (tier.months != null) limit = "every " + tier.months + " months";
    return range && limit ? range + ": " + limit : range || limit;
  }).join(" · ");
}

/**
 * A line's age rule as two editable bounds, when it is one: no tier, or a
 * single tier. Sun Life's several "Limits - N" tiers are shown, not edited.
 */
export function ageBoundsFromTiers(tiers) {
  const list = Array.isArray(tiers) ? tiers : [];
  if (list.length > 1) return { editable: false, min: "", max: "" };
  const tier = list[0] || {};
  return {
    editable: true,
    min: tier.min_age == null ? "" : String(tier.min_age),
    max: tier.max_age == null ? "" : String(tier.max_age),
  };
}

/** The tiers to save for two edited bounds; keeps the single tier's limit. */
export function tiersFromAgeBounds(existing, min, max) {
  const blank = function (v) { return v == null || String(v).trim() === ""; };
  if (blank(min) && blank(max)) return null;
  const tier = (Array.isArray(existing) && existing.length === 1 && existing[0]) || {};
  return [{
    min_age: blank(min) ? null : parseInt(min, 10),
    max_age: blank(max) ? null : parseInt(max, 10),
    count: tier.count == null ? null : tier.count,
    months: tier.months == null ? null : tier.months,
  }];
}

function isBlank(v) {
  return v == null || String(v).trim() === "";
}

/**
 * The status a % says when nobody picked one: above 0 is Covered, 0 is Not
 * covered. "" when there's no % (or it isn't a number), or a status was
 * chosen — a chosen status is never replaced.
 */
export function statusFromPercent(percentage, chosen) {
  if (!isBlank(chosen) || isBlank(percentage)) return chosen || "";
  const pct = Number(percentage);
  if (isNaN(pct)) return "";
  return pct > 0 ? "covered" : "not_covered";
}

/**
 * Why an edited line can't be saved, or null. Every field is optional — a
 * line can be saved with only what the carrier said (or nothing, still to
 * ask). What's refused is what can't be right: a value that isn't a number or
 * is out of range, "0 every 0 months", a % on a line marked Not covered,
 * ages the wrong way round, a code that isn't one.
 */
export function lineEditError(edit) {
  const e = edit || {};
  const pct = isBlank(e.percentage_covered) ? null : Number(e.percentage_covered);
  if (pct !== null && (isNaN(pct) || pct < 0 || pct > 100)) return "Coverage must be between 0 and 100.";
  if (e.coverage_status === "not_covered" && pct !== null && pct > 0) {
    return "A line marked Not covered can't also have a coverage %. Clear the % or change the status.";
  }
  const whole = function (v) { return /^\d+$/.test(String(v).trim()); };
  if (!e.isFrequencyUnlimited && ((!isBlank(e.max_occurrences) && !whole(e.max_occurrences))
    || (!isBlank(e.coverage_duration_months) && !whole(e.coverage_duration_months)))) {
    return "How often takes whole numbers, e.g. 1 every 9 months — or leave it blank.";
  }
  if (!e.isFrequencyUnlimited && String(e.max_occurrences).trim() === "0" && String(e.coverage_duration_months).trim() === "0") {
    return "\"0 every 0 months\" says nothing. Leave both blank when there's no limit.";
  }
  const min = isBlank(e.age_min) ? null : Number(e.age_min);
  const max = isBlank(e.age_max) ? null : Number(e.age_max);
  if ((min !== null && (isNaN(min) || min < 0 || min > 120)) || (max !== null && (isNaN(max) || max < 0 || max > 120))) {
    return "Ages must be whole numbers between 0 and 120.";
  }
  if (min !== null && max !== null && min > max) return "The \"from\" age is above the \"to\" age.";
  // A limit the line shares with other codes: "2 every 12 months combined"
  // means nothing until it says which codes. Codes a scraper stored are
  // checked only once someone retypes them.
  const shared = parseCodeList(e.shared_limit_codes_text);
  const codesEdited = (e.shared_limit_codes_text || "") !== (e.shared_limit_codes_initial || "");
  if (codesEdited && shared.bad.length) {
    return "\"" + shared.bad[0] + "\" isn't a procedure code. List the codes that share the limit, e.g. 01204, 01205.";
  }
  const wholePositive = function (v) { return /^\d+$/.test(String(v).trim()) && Number(v) > 0; };
  if ((!isBlank(e.shared_limit_count) && !wholePositive(e.shared_limit_count))
    || (!isBlank(e.shared_limit_months) && !wholePositive(e.shared_limit_months))) {
    return "A combined limit needs whole numbers above 0 — or leave it blank.";
  }
  return null;
}
